import { useState, useRef } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Receipt, Plus, Trash2, ImageIcon, Paperclip, Share2, Download, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PersonSelect } from "@/components/EntityDialogs";
import { ShareDialog } from "@/components/ShareDialog";
import { uploadFile } from "@/lib/upload";

const CATEGORIES = ["Travel", "Accommodation", "Meals", "Education", "Household", "Insurance", "Medical", "Other"];
const STATUS = ["pending", "approved", "reimbursed"];
const Field = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-slate-700">{label}</Label>{children}</div>);

const ReceiptUpload = ({ value, onChange }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Receipt must be under 10MB");
    setUploading(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="receipt-file-input" />
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="receipt" className="h-16 w-16 rounded-lg object-cover border border-slate-200" />
          <Button type="button" variant="outline" size="sm" onClick={() => onChange("")} data-testid="receipt-remove">Remove</Button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading} data-testid="receipt-upload-btn"
          className="w-full rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-400 hover:border-slate-300 transition-colors disabled:opacity-60">
          {uploading ? <><Loader2 className="h-6 w-6 mx-auto mb-1.5 animate-spin" /> Uploading…</> : <><ImageIcon className="h-6 w-6 mx-auto mb-1.5" /> Attach a receipt photo</>}
        </button>
      )}
    </div>
  );
};

const ExpenseDialog = ({ open, onOpenChange }) => {
  const { addExpense } = useFgp();
  const blank = { date: new Date().toISOString().slice(0, 10), merchant: "", category: "Travel", amount: "", currency: "£", status: "pending", person: "Household", notes: "", receipt: "" };
  const [f, setF] = useState(blank);
  const submit = () => {
    if (!f.merchant.trim()) return toast.error("Merchant is required");
    const amt = parseFloat(f.amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    addExpense({ ...f, amount: amt });
    toast.success("Expense added");
    onOpenChange(false); setF(blank);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add expense</DialogTitle>
          <DialogDescription>Log a household or travel expense with an optional receipt.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <ReceiptUpload value={f.receipt} onChange={(v) => setF({ ...f, receipt: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Merchant"><Input value={f.merchant} onChange={(e) => setF({ ...f, merchant: e.target.value })} data-testid="expense-merchant-input" placeholder="e.g. British Airways" /></Field>
            <Field label="Date"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Amount"><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount-input" placeholder="0.00" /></Field>
            <Field label="Currency">
              <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["£", "$", "€", "CHF"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Charged to"><PersonSelect value={f.person} onChange={(v) => setF({ ...f, person: v })} testid="expense-person-select" /></Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="expense-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Notes"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="expense-save-btn">Add expense</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Expenses() {
  const { expenses, updateExpense, deleteExpense } = useFgp();
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const sum = (arr) => arr.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const fmtMoney = (n) => "£" + n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pending = expenses.filter((e) => e.status === "pending");
  const reimbursed = expenses.filter((e) => e.status === "reimbursed");

  const shareUrl = `${window.location.origin}/app/expenses`;
  const shareHtml = `
    <div class="totals">
      <div class="box"><div class="lbl">Total logged</div><div class="val">${fmtMoney(sum(expenses))}</div></div>
      <div class="box"><div class="lbl">Pending</div><div class="val" style="color:#d97706">${fmtMoney(sum(pending))}</div></div>
      <div class="box"><div class="lbl">Reimbursed</div><div class="val" style="color:#16a34a">${fmtMoney(sum(reimbursed))}</div></div>
      <div class="box"><div class="lbl">Entries</div><div class="val">${expenses.length}</div></div>
    </div>
    <table><thead><tr><th>Date</th><th>Merchant</th><th>Category</th><th>Charged to</th><th class="num">Amount</th><th>Status</th></tr></thead>
    <tbody>${expenses.map((e) => `<tr><td>${new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td><td>${e.merchant}</td><td>${e.category}</td><td>${e.person}</td><td class="num">${e.currency}${Number(e.amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td><td><span class="pill">${e.status}</span></td></tr>`).join("")}</tbody></table>`;

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Date", "Merchant", "Category", "Charged to", "Currency", "Amount", "Status", "Notes"];
    const rows = expenses.map((e) => [e.date, e.merchant, e.category, e.person, e.currency, Number(e.amount).toFixed(2), e.status, e.notes || ""].map(esc).join(","));
    const csv = [header.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${expenses.length} expense${expenses.length === 1 ? "" : "s"} to CSV`);
  };

  return (
    <div>
      <PageHeader title="Expenses" subtitle="Track, categorise and reconcile household and travel spend."
        actions={<>
          {expenses.length > 0 && <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="export-expenses-csv"><Download className="h-4 w-4" /> Export CSV</Button>}
          {expenses.length > 0 && <Button variant="outline" onClick={() => setShareOpen(true)} className="gap-2" data-testid="share-expenses"><Share2 className="h-4 w-4" /> Share report</Button>}
          <Button onClick={() => setOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-expense"><Plus className="h-4 w-4" /> Add expense</Button>
        </>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total logged", value: fmtMoney(sum(expenses)), tone: "text-slate-900" },
          { label: "Pending", value: fmtMoney(sum(pending)), tone: "text-amber-600" },
          { label: "Reimbursed", value: fmtMoney(sum(reimbursed)), tone: "text-emerald-600" },
          { label: "Entries", value: expenses.length, tone: "text-slate-900" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
            <p className={`mt-2 font-heading text-2xl font-semibold ${s.tone}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {expenses.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Receipt className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No expenses yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Log spend and attach receipts to keep reconciliation simple.</p>
          <Button onClick={() => setOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="empty-add-expense"><Plus className="h-4 w-4" /> Add your first expense</Button>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Merchant</TableHead>
                <TableHead className="hidden sm:table-cell">Category</TableHead>
                <TableHead className="hidden md:table-cell">Charged to</TableHead>
                <TableHead className="hidden lg:table-cell">Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {e.receipt ? <img src={e.receipt} alt="" className="h-8 w-8 rounded object-cover border border-slate-200" /> : <span className="h-8 w-8 rounded bg-slate-100 text-slate-300 flex items-center justify-center"><Paperclip className="h-3.5 w-3.5" /></span>}
                      <div><p className="text-sm font-medium text-slate-800">{e.merchant}</p><p className="text-xs text-slate-400 sm:hidden">{e.category}</p></div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-slate-500 text-sm">{e.category}</TableCell>
                  <TableCell className="hidden md:table-cell text-slate-500 text-sm">{e.person}</TableCell>
                  <TableCell className="hidden lg:table-cell text-slate-400 text-sm">{new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</TableCell>
                  <TableCell className="text-right font-medium text-slate-800">{e.currency}{Number(e.amount).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    <Select value={e.status} onValueChange={(v) => { updateExpense(e.id, { status: v }); toast.success(`Marked ${v}`); }}>
                      <SelectTrigger className="h-8 w-[130px] border-0 bg-transparent p-0 focus:ring-0" data-testid={`expense-status-${e.id}`}><StatusBadge status={e.status} /></SelectTrigger>
                      <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`expense-status-opt-${e.id}-${s}`}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => { deleteExpense(e.id); toast.success("Expense deleted"); }} className="text-slate-400 hover:text-rose-600" data-testid={`delete-expense-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </motion.div>
      )}

      <ExpenseDialog open={open} onOpenChange={setOpen} />
      {shareOpen && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} testidPrefix="share-expenses"
        docTitle="Expense Report" docSubtitle={`${expenses.length} entries · generated from Swishr Central`} bodyHtml={shareHtml} shareUrl={shareUrl} />}
    </div>
  );
}
